/**
 * Sidebar Navigation Component
 * 
 * Persistent left sidebar with tab navigation matching lore-native design.
 * Uses custom SVG icons and Lore branding.
 * Features a user profile section at the bottom that links to settings/menu.
 * 
 * @module components/layout/Sidebar
 */

import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useProfile } from '@/contexts/ProfileContext';
import { useNavBadgesOptional } from '@/contexts/NavBadgesContext';
import type { NavBadgeSection } from '@/types/navBadges';
import { getAvatarUrl } from '@/utils/avatarUrl';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import HomeIcon from '@mui/icons-material/Home';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import './Sidebar.css';

interface TabItem {
  path: string;
  label: string;
  iconOutline: string | React.ReactNode;
  iconSolid: string | React.ReactNode;
  isMuiIcon?: boolean;
}

const TABS: TabItem[] = [
  {
    path: '/home',
    label: 'Home',
    iconOutline: <HomeOutlinedIcon />,
    iconSolid: <HomeIcon />,
    isMuiIcon: true,
  },
  {
    path: '/rewards',
    label: 'Rewards',
    iconOutline: '/icons/tabs/rewards_outline.svg',
    iconSolid: '/icons/tabs/rewards_solid.svg',
  },
  {
    path: '/lorebot',
    label: 'LoreBot',
    iconOutline: '/icons/tabs/lorebot_outline.svg',
    iconSolid: '/icons/tabs/lorebot_solid.svg',
  },
  {
    path: '/more',
    label: 'More',
    iconOutline: <MoreHorizOutlinedIcon />,
    iconSolid: <MoreHorizIcon />,
    isMuiIcon: true,
  },
];

/** Map tab path to nav-badge section (API key). Home has no badge. */
function pathToBadgeSection(path: string): NavBadgeSection | null {
  switch (path) {
    case '/rewards': return 'rewards';
    case '/lorebot': return 'lorebot';
    case '/more': return 'more';
    default: return null;
  }
}

/**
 * Get initials from username only (NO email fallback for privacy)
 * Extracts first letter of each word in the username (handles underscores, hyphens, spaces)
 * Returns maximum of 2 letters
 */
function getInitials(name: string | undefined): string {
  if (name) {
    // Split on common separators (underscore, hyphen, space) and get first letter of each part
    const parts = name.split(/[_\-\s]+/).filter(part => part.length > 0);
    if (parts.length >= 2) {
      // If multiple parts, use first letter of first two parts (max 2)
      return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
    } else if (parts.length === 1) {
      // If single part, use first letter only (max 2, but only 1 available)
      return parts[0].charAt(0).toUpperCase();
    }
  }
  // Return empty - don't expose email on anonymous platform
  return '';
}

/**
 * Get display name from profile only (NO email fallback for privacy)
 */
function getDisplayName(loreName: string | undefined): string {
  // Only use lore_user_name - never expose email
  if (loreName) {
    return loreName;
  }
  // Return empty while loading - don't show email
  return '';
}

export const Sidebar: React.FC = () => {
  const { profile } = useProfile();
  const navBadges = useNavBadgesOptional();
  const location = useLocation();
  const navigate = useNavigate();

  const isMenuActive = location.pathname.startsWith('/account');

  // Get display info from profile only (NO email fallback for privacy)
  const displayName = getDisplayName(profile?.lore_user_name);
  const initials = getInitials(profile?.lore_user_name);
  const [avatarError, setAvatarError] = useState(false);

  // Avatar URL from sso_object_id. On localhost we use /avatar-proxy to avoid net::ERR_BLOCKED_BY_ORB.
  const avatarUrl = getAvatarUrl(profile?.sso_object_id);

  // Only show avatar image if URL exists and hasn't errored
  const showAvatarImage = avatarUrl && !avatarError;

  const handleProfileClick = () => {
    navigate('/account');
  };

  const handleAvatarError = () => {
    console.warn('[Sidebar] Avatar image failed to load, falling back to initials');
    setAvatarError(true);
  };

  return (
    <aside className="sidebar">
      {/* Logo Header */}
      <div className="sidebar-header">
        <img src="/logos/lore-wordmark-horizontal-color.png" alt="Lore" className="sidebar-wordmark" />
      </div>

      {/* Navigation Tabs */}
      <nav className="sidebar-nav">
        {TABS.map((tab) => {
          const isActive = location.pathname === tab.path || location.pathname.startsWith(tab.path + '/');
          const badgeSection = pathToBadgeSection(tab.path);
          const showBadge = Boolean(badgeSection && navBadges?.badges[badgeSection]);

          // MFE tabs use hard refresh to prevent state pollution between MFEs
          const isMfeTab = ['/rewards', '/lorebot', '/community'].includes(tab.path);

          if (isMfeTab) {
            return (
              <a
                key={tab.path}
                href={tab.path}
                className={`sidebar-tab ${isActive ? 'sidebar-tab-active' : ''}`}
              >
                <span className="sidebar-tab-icon-wrap">
                  <img
                    src={(isActive ? tab.iconSolid : tab.iconOutline) as string}
                    alt={tab.label}
                    className="sidebar-tab-icon"
                  />
                  {showBadge && <span className="sidebar-nav-badge" aria-hidden />}
                </span>
                <span className="sidebar-tab-label">{tab.label}</span>
              </a>
            );
          }

          // Home tab: force hard refresh when clicking if already on home
          if (tab.path === '/home') {
            return (
              <a
                key={tab.path}
                href={tab.path}
                className={`sidebar-tab ${location.pathname === '/home' ? 'sidebar-tab-active' : ''}`}
                onClick={(e) => {
                  if (location.pathname === '/home') {
                    e.preventDefault();
                    window.location.href = '/home';
                  }
                }}
              >
                <span className="sidebar-tab-icon-wrap">
                  <span className="sidebar-tab-icon sidebar-tab-icon-mui">
                    {location.pathname === '/home' ? tab.iconSolid : tab.iconOutline}
                  </span>
                </span>
                <span className="sidebar-tab-label">{tab.label}</span>
              </a>
            );
          }

          return (
            <NavLink
              key={tab.path}
              to={tab.path}
              className={({ isActive }) =>
                `sidebar-tab ${isActive ? 'sidebar-tab-active' : ''}`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="sidebar-tab-icon-wrap">
                    {tab.isMuiIcon ? (
                      <span className="sidebar-tab-icon sidebar-tab-icon-mui">
                        {isActive ? tab.iconSolid : tab.iconOutline}
                      </span>
                    ) : (
                      <img
                        src={(isActive ? tab.iconSolid : tab.iconOutline) as string}
                        alt={tab.label}
                        className="sidebar-tab-icon"
                      />
                    )}
                    {showBadge && <span className="sidebar-nav-badge" aria-hidden />}
                  </span>
                  <span className="sidebar-tab-label">{tab.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User Profile Section */}
      <div
        className={`sidebar-profile ${isMenuActive ? 'sidebar-profile-active' : ''}`}
        onClick={handleProfileClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleProfileClick()}
      >
        <span className="sidebar-profile-avatar-wrap">
          {showAvatarImage ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className="sidebar-avatar-image"
              onError={handleAvatarError}
            />
          ) : (
            <div className="sidebar-avatar">
              {initials}
            </div>
          )}
          {navBadges?.badges.account && <span className="sidebar-nav-badge sidebar-nav-badge--profile" aria-hidden />}
        </span>
        <span className="sidebar-username">{displayName}</span>
      </div>
    </aside>
  );
};
